import React, { useState, useEffect, FC } from 'react';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import { Group } from '@vromo/models';
import { GroupTree } from './TeamEditor';
import styled from '@emotion/styled';
import theme from '@vromo/ui/components/theme';
import { lighten } from 'polished';
import sortBy from 'lodash/sortBy';
import { css } from 'emotion';
import Tooltip from '@vromo/ui/components/Tooltip';
import { qaLabelTeams } from './../../../../common-qa-labels/teams';
import queryString from 'query-string';
import Debug from '@vromo/ui/components/Debug';

const TeamSelector = styled.div<{ selected: boolean }>`
    font-size: 14px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 5px;
    color: #676a6c;
    align-self: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 350px;
    transition: background-color 200ms ease-in-out;
    background-color: ${({ selected }) =>
        selected ? `${lighten(0.3, theme.primary)}` : 'transparent'};

    &:hover {
        background-color: ${lighten(0.3, theme.primary)};
    }
`;

interface Props {
    groupTree: GroupTree;
    depth: number;
    selected: number | null | undefined;
    navigate: (group: Group) => void;
}

const DEBUG_MODE = 'debug' in queryString.parse(window.location.search);

const SubTeamExplorer: FC<Props> = ({ groupTree, depth, navigate, selected }) => {
    const [expanded, setExpanded] = useState<boolean>(groupTree.expanded);
    const [isSelected, setIsSelected] = useState<boolean>(false);
    const { group, children } = groupTree;

    useEffect(() => {
        setExpanded(groupTree.expanded || depth == 0);
    }, [depth, groupTree]);

    useEffect(() => {
        setIsSelected(selected === group.id);
    }, [group.id, selected]);

    const select = () => {
        navigate(group);
        setIsSelected(true);
    };

    const toggleExpand = () => {
        groupTree.expanded = !expanded;
        setExpanded(!expanded);
    };

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 5,
                    marginLeft: depth * 20,
                    marginRight: 25
                }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                    <FontAwesome
                        xs
                        icon={expanded ? 'minus' : 'plus'}
                        onClick={toggleExpand}
                        className={css`
                            padding: 8px;
                            margin-right: 5px;
                            cursor: ${children.length ? 'pointer' : 'default'};
                            border-radius: 5px;
                            color: ${theme.primary};
                            opacity: ${children.length > 0 ? 1 : 0};
                            transition: background-color 200ms ease-in-out;

                            &:hover {
                                background-color: ${children.length
                                    ? lighten(0.3, theme.primary)
                                    : 'transparent'};
                            }
                        `}
                    />

                    <a onClick={select} style={{ display: 'flex' }}>
                        <TeamSelector selected={isSelected} data-testid={qaLabelTeams.teamGroupItem}>
                            {group.name}
                            {DEBUG_MODE && 
                                <Debug>GroupId: {groupTree?.group?.id}</Debug>
                            }
                        </TeamSelector>
                        {group.parents && group.parents?.length > 1 && (
                            <Tooltip
                                label={`This team has an additional ${
                                    group.parents.length > 2
                                        ? `${group.parents.length} parents.`
                                        : 'parent.'
                                }`}>
                                <sup
                                    className={css`
                                        margin: 0px 3px;
                                    `}>
                                    +{group.parents.length - 1}
                                </sup>
                            </Tooltip>
                        )}
                    </a>
                </div>
            </div>
            {expanded && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {sortBy(children, (g) => g.group.name?.trim().toLowerCase()).map((g) => (
                        <SubTeamExplorer
                            key={g.group.id}
                            groupTree={g}
                            depth={depth + 1}
                            navigate={navigate}
                            selected={selected}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default SubTeamExplorer;
